import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { Icon, Typography } from '@/components';

// ─── Types ───────────────────────────────────────────────────────────────────

type LegacySlotsCardProps = {
  readonly slots: string[];
  readonly meetingTime?: string | null;
};

// ─── Component ───────────────────────────────────────────────────────────────

export function LegacySlotsCard({ slots, meetingTime }: LegacySlotsCardProps) {
  const { t } = useTranslation(['offers']);

  const isConfirmed = !!meetingTime;
  const isManual = meetingTime === 'Other';

  const statusConfig = useMemo(() => {
    if (isManual) {
      return {
        icon: 'clock' as const,
        iconBg: 'bg-amber-500',
        title: t('Meeting pending'),
        subtitle: t('Our team will coordinate the meeting manually. You will be contacted shortly.'),
      };
    }
    if (isConfirmed) {
      return {
        icon: 'check' as const,
        iconBg: 'bg-emerald-600',
        title: t('Meeting confirmed'),
        subtitle: t('The meeting slot has been confirmed.'),
      };
    }
    return {
      icon: 'calendar' as const,
      iconBg: 'bg-amber-500',
      title: t('Availability shared'),
      subtitle: t('Waiting for the buyer to select a meeting slot.'),
    };
  }, [isConfirmed, isManual, t]);

  return (
    <Card className="mb-6 border border-emerald-200 bg-emerald-50/30 backdrop-blur-sm">
      <CardContent>
        {/* Header */}
        <div className="mb-6 flex flex-col items-center text-center">
          <div className={`mb-3 flex h-12 w-12 items-center justify-center rounded-full ${statusConfig.iconBg}`}>
            <Icon name={statusConfig.icon} className="h-6 w-6 text-white" />
          </div>
          <Typography as="h3" size="body-1" className="font-bold">
            {statusConfig.title}
          </Typography>
          <Typography size="body-3" className="mt-1 text-slate-500">
            {statusConfig.subtitle}
          </Typography>
        </div>

        <hr className="border-emerald-100" />

        {/* Slots list */}
        <div className="mt-4">
          <Typography as="p" size="body-3" className="mb-3 font-bold tracking-wide uppercase">
            {t('Proposed slots')}
          </Typography>

          <div className="space-y-2">
            {slots.map((slot, index) => {
              const isSelected = isConfirmed && meetingTime === slot;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  {isSelected && <Icon name="check" className="h-4 w-4 shrink-0 text-emerald-600" />}
                  <Typography
                    size="body-3"
                    className={isSelected ? 'font-semibold text-emerald-700' : 'text-slate-700'}
                  >
                    {slot}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
